/* =========================================================
   Import from Excel.

   Download a per-entity .xlsx template, fill it, upload it
   back. The prototype faked the parse; here the file is really
   read with SheetJS, its columns are matched against the
   entity's field schema, and each row is validated before the
   preview — so the valid/error counts are true.
   ========================================================= */
import React, { useState } from 'react';
import type { FieldSchema } from '@/types';
import { ic, sized } from '@/components/Icons';
import { Section } from '@/components/ui';
import { useI18n } from '@/i18n';
import { IS_API, ds } from '@/data';
import { instructorSchema, orgSchema, siteSchema, validateImportField } from '@/lib/fields';
import { IMPORT_CHUNK, importRows, type ImportRowError } from '@/lib/api';
import { download as downloadXlsx, parseSpreadsheet } from '@/lib/xlsx';
import { useAppStore } from '@/store/useAppStore';

type Entity = 'orgs' | 'sites' | 'instructors';

const SCHEMA: Record<Entity, FieldSchema> = {
  orgs: orgSchema,
  sites: siteSchema,
  instructors: instructorSchema,
};

const TPL: Record<Entity, { sheet: string; file: string }> = {
  orgs: { sheet: 'Organizaciones', file: 'plantilla_organizaciones' },
  sites: { sheet: 'Sites', file: 'plantilla_sites' },
  instructors: { sheet: 'Instructores', file: 'plantilla_instructores' },
};

const dedupe = (a: string[]) => a.filter((v, i) => a.indexOf(v) === i);

interface PreviewRow extends Record<string, string | undefined> {
  __error?: string;
}

interface Preview {
  headers: string[];
  /** Headers in the file that match no schema field. */
  unmapped: string[];
  /** Solo las primeras filas: la tabla es para mirar, no para procesar. */
  rows: PreviewRow[];
  /** Todas las filas sin error de formato — esto es lo que se envía. */
  sendable: Record<string, string>[];
  valid: number;
  errors: number;
}

/** Resultado de comprobar o aplicar, ya sumado entre tandas. */
interface Outcome {
  dryRun: boolean;
  applied: boolean;
  created: number;
  updated: number;
  unchanged: number;
  errors: ImportRowError[];
}

export function ExcelImportCard() {
  const { lang, t } = useI18n();
  const addToast = useAppStore((s) => s.addToast);
  const [entity, setEntity] = useState<Entity>('sites');
  const [tplEntity, setTplEntity] = useState<Entity>('sites');
  /** Bajar la plantilla con lo que ya hay dentro, para corregir en vez de
   *  dar de alta. Es lo que hace falta para repasar el padrón entero. */
  const [conDatos, setConDatos] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [busy, setBusy] = useState(false);

  // Aplicar exige haber comprobado antes: la validación del navegador no
  // sabe si el país existe ni si la organización de un site está creada.
  const comprobado = outcome?.dryRun === true && outcome.errors.length === 0;

  /** Las filas que hay hoy en el padrón, en el orden de las columnas.
   *
   *  La plantilla salía siempre vacía, y con ella solo se pueden CREAR
   *  registros: para corregir trescientas organizaciones había que
   *  teclear a mano el identificador de cada una. Y la exportación de
   *  Reportes no servía de origen, porque encabeza con nombres humanos
   *  («Nombre de la organización») y el importador espera claves de campo
   *  («OrgName»): el viaje de ida y vuelta estaba roto.
   *
   *  Con los datos dentro, el ciclo es el que espera cualquiera: bajar,
   *  corregir en Excel, volver a subir. */
  function filasActuales(ent: Entity, keys: string[]): string[][] {
    const registros: Record<string, unknown>[] =
      ent === 'orgs' ? (ds.listOrgs() as unknown as Record<string, unknown>[])
      : ent === 'sites' ? (ds.listSites() as unknown as Record<string, unknown>[])
      : (ds.listInstructors() as unknown as Record<string, unknown>[]);
    return registros.map((r) => keys.map((k) => {
      const v = r[k];
      return v == null || v === false ? '' : v === true ? 'true' : String(v);
    }));
  }

  function downloadTemplate() {
    const cfg = TPL[tplEntity];
    const schema = SCHEMA[tplEntity];
    const keys = dedupe(schema.map((f) => f.key));
    // row 1: machine headers (field keys) · row 2: human labels (hint)
    const labelRow = keys.map((k) => {
      const f = schema.find((x) => x.key === k);
      return f ? (lang === 'es' ? f.label_es : f.label_en) : k;
    });
    const datos = conDatos ? filasActuales(tplEntity, keys) : [];
    downloadXlsx(cfg.file, [keys, labelRow, ...datos], cfg.sheet);
    addToast({
      kind: 'template',
      title: lang === 'es' ? 'Plantilla descargada' : 'Template downloaded',
      body: conDatos
        ? `${cfg.file}.xlsx · ${datos.length} ${lang === 'es' ? 'filas con los datos de hoy' : 'rows with today\u2019s data'}`
        : `${cfg.file}.xlsx · ${lang === 'es' ? 'vacía, para dar de alta' : 'blank, for new records'}`,
    });
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setFile(f);
    setBusy(true);
    try {
      const parsed = await parseSpreadsheet(f);
      const schema = SCHEMA[entity];
      const known = new Set(schema.map((x) => x.key));

      // the template's second row repeats the human labels — skip it
      const dataRows = parsed.rows.filter((r) => {
        const first = parsed.headers[0];
        return !(first && r[first] && schema.some((s) => s.label_es === r[first] || s.label_en === r[first]));
      });

      const headers = parsed.headers.filter((h) => known.has(h));
      const unmapped = parsed.headers.filter((h) => h && !known.has(h));

      // Se valida el archivo entero, no solo lo que cabe en la tabla: los
      // recuentos de válidas y con error tienen que ser los de verdad, y
      // lo que se envía son todas las filas buenas.
      const todas: PreviewRow[] = dataRows.map((r) => {
        const row: PreviewRow = {};
        headers.forEach((h) => {
          row[h] = r[h] ?? '';
        });
        // first failing field decides the row's error label
        for (const fld of schema) {
          if (!headers.includes(fld.key)) continue;
          const err = validateImportField(fld, row[fld.key]);
          if (err) {
            const label = lang === 'es' ? fld.label_es : fld.label_en;
            row.__error = `${label}: ${t('field.' + err)}`;
            break;
          }
        }
        return row;
      });

      const rows = todas.slice(0, 50);
      const sendable = todas
        .filter((r) => !r.__error)
        .map((r) => {
          const limpio: Record<string, string> = {};
          headers.forEach((h) => { limpio[h] = r[h] ?? ''; });
          return limpio;
        });

      if (!headers.length) {
        addToast({
          kind: 'err',
          title: lang === 'es' ? 'No se reconocieron columnas' : 'No columns recognised',
          body: lang === 'es'
            ? 'Usa la plantilla de la entidad: la primera fila debe traer las claves de campo.'
            : 'Use the entity template: the first row must carry the field keys.',
        });
      }

      setOutcome(null);
      setPreview({
        headers,
        unmapped,
        rows,
        sendable,
        valid: sendable.length,
        errors: todas.length - sendable.length,
      });
    } catch {
      setPreview(null);
      addToast({
        kind: 'err',
        title: lang === 'es' ? 'No se pudo leer el archivo' : 'Could not read the file',
        body: lang === 'es' ? 'Comprueba que sea .xlsx o .csv válido.' : 'Check that it is a valid .xlsx or .csv.',
      });
    } finally {
      setBusy(false);
    }
  }

  /** Envía las filas en tandas y suma los resultados.
   *
   *  Cada tanda es una transacción: hasta 1000 filas —el caso normal de un
   *  maestro— es todo o nada. Por encima, una tanda posterior puede fallar
   *  con las anteriores ya aplicadas; por eso se comprueba antes. */
  async function enviar(dryRun: boolean) {
    if (!preview || !preview.sendable.length) return;
    setBusy(true);
    const total: Outcome = { dryRun, applied: false, created: 0, updated: 0, unchanged: 0, errors: [] };
    try {
      for (let i = 0; i < preview.sendable.length; i += IMPORT_CHUNK) {
        const tanda = preview.sendable.slice(i, i + IMPORT_CHUNK);
        const res = await importRows(entity, tanda, dryRun);
        if (!res.ok) {
          setOutcome(null);
          addToast({
            kind: 'err',
            title: dryRun
              ? (lang === 'es' ? 'La comprobación falló' : 'The check failed')
              : (lang === 'es' ? 'No se importó' : 'Not imported'),
            body: res.message,
          });
          return;
        }
        total.applied = total.applied || res.summary.applied;
        total.created += res.summary.created;
        total.updated += res.summary.updated;
        total.unchanged += res.summary.unchanged;
        // los números de fila son relativos a su tanda
        total.errors.push(...res.summary.errors.map((e) => ({ ...e, row: e.row + i })));
      }
      setOutcome(total);

      if (dryRun) {
        addToast({
          kind: total.errors.length ? 'err' : 'ok',
          title: total.errors.length
            ? (lang === 'es' ? 'Hay filas que no entrarían' : 'Some rows would not go in')
            : (lang === 'es' ? 'Comprobación correcta' : 'Check passed'),
          body: `${total.created} ${lang === 'es' ? 'altas' : 'new'} · ${total.updated} ${lang === 'es' ? 'cambios' : 'changed'} · ${total.unchanged} ${lang === 'es' ? 'sin cambio' : 'unchanged'}`,
        });
      } else {
        addToast({
          kind: 'import',
          title: lang === 'es' ? 'Importación aplicada' : 'Import applied',
          body: `${total.created} ${lang === 'es' ? 'altas' : 'created'} · ${total.updated} ${lang === 'es' ? 'actualizadas' : 'updated'}`,
        });
        setFile(null);
        setPreview(null);
      }
    } finally {
      setBusy(false);
    }
  }

  /** Sin backend no hay dónde guardar: se dice, en vez de fingir. */
  function applyInMemory() {
    if (!preview) return;
    addToast({
      kind: 'import',
      title: lang === 'es' ? 'Solo demostración' : 'Demo only',
      body: lang === 'es'
        ? `${preview.valid} filas válidas. En la demostración no se guarda nada.`
        : `${preview.valid} valid rows. Nothing is saved in the demo.`,
    });
    setFile(null);
    setPreview(null);
  }

  const entityOptions: { v: Entity; l: string }[] = [
    { v: 'orgs', l: lang === 'es' ? 'Organizaciones' : 'Organizations' },
    { v: 'sites', l: 'Sites' },
    { v: 'instructors', l: lang === 'es' ? 'Instructores' : 'Instructors' },
  ];

  return (
    <Section
      title={t('admin.import')}
      tone="var(--brand-primary)"
      right={preview ? <span className="chip ok">{preview.valid} ✓ · {preview.errors} {lang === 'es' ? 'errores' : 'errors'}</span> : null}
    >
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* download template */}
        <div className="card" style={{ padding: 14, background: 'var(--surface-2)', border: '1px solid var(--line)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{lang === 'es' ? 'Plantilla de carga' : 'Upload template'}</div>
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>
                {lang === 'es'
                  ? 'Descarga el .xlsx de la entidad, corrígelo y vuelve a subirlo. Con «incluir los datos actuales» baja lo que ya hay, para repasarlo; sin marcar, baja vacía para dar de alta.'
                  : 'Download the entity .xlsx, correct it and upload it back. With “include current data” it brings what already exists, to review it; unchecked, it comes blank for new records.'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>{lang === 'es' ? 'Entidad destino' : 'Target entity'}</span>
                <select
                  value={tplEntity}
                  onChange={(e) => setTplEntity(e.target.value as Entity)}
                  style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--line-2)', fontSize: 13, background: 'var(--surface)' }}
                >
                  {entityOptions.map((o) => (
                    <option key={o.v} value={o.v}>{o.l}</option>
                  ))}
                </select>
              </label>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, paddingBottom: 7, cursor: 'pointer' }}
                data-tip={lang === 'es'
                  ? 'Con los datos dentro puedes corregir lo que ya existe. Vacía, solo sirve para dar de alta registros nuevos.'
                  : 'With the data inside you can correct what already exists. Blank, it only serves to create new records.'}
              >
                <input type="checkbox" checked={conDatos} onChange={(e) => setConDatos(e.target.checked)} />
                {lang === 'es' ? 'Incluir los datos actuales' : 'Include current data'}
              </label>
              <button className="btn primary" onClick={downloadTemplate}>
                {ic.download} {lang === 'es' ? 'Descargar plantilla' : 'Download template'} (.xlsx)
              </button>
            </div>
          </div>
        </div>

        <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{t('admin.import_help')}</div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>{t('admin.target_entity')}</span>
            <select
              value={entity}
              onChange={(e) => {
                setEntity(e.target.value as Entity);
                setPreview(null);
                setFile(null);
              }}
              style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--line-2)', fontSize: 13, background: 'var(--surface)' }}
            >
              {entityOptions.map((o) => (
                <option key={o.v} value={o.v}>{o.l}</option>
              ))}
            </select>
          </label>

          <label className="btn" style={{ cursor: 'pointer', marginTop: 14 }}>
            {ic.upload} {busy ? t('common.loading') : file ? file.name : t('admin.choose_file')}
            <input type="file" accept=".xlsx,.xls,.csv" onChange={onFile} style={{ display: 'none' }} />
          </label>
          <span style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 14 }}>.xlsx · .csv</span>
        </div>

        {preview ? (
          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', background: 'var(--surface-2)', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ color: 'var(--text-2)' }}>{ic.excel}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{t('admin.map_columns')}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>
                    {preview.headers.length} {lang === 'es' ? 'columnas mapeadas' : 'columns mapped'} ·{' '}
                    {preview.valid + preview.errors}{' '}
                    {lang === 'es' ? 'filas en el archivo' : 'rows in the file'}
                    {preview.valid + preview.errors > preview.rows.length
                      ? ` (${lang === 'es' ? 'se muestran las' : 'showing the first'} ${preview.rows.length})`
                      : ''}
                    {preview.unmapped.length
                      ? ` · ${preview.unmapped.length} ${lang === 'es' ? 'sin mapear' : 'unmapped'}`
                      : ''}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span className="chip ok">{preview.valid} {t('admin.rows_valid')}</span>
                <span className="chip err">{preview.errors} {t('admin.rows_errors')}</span>
              </div>
            </div>

            {preview.unmapped.length ? (
              <div style={{ padding: '8px 14px', fontSize: 11.5, color: 'var(--text-2)', background: 'var(--st-edit-l)', borderBottom: '1px solid var(--line)' }}>
                {sized(ic.warn, 12)}{' '}
                {lang === 'es' ? 'Columnas ignoradas (no existen en el esquema): ' : 'Ignored columns (not in the schema): '}
                <span className="mono">{preview.unmapped.join(', ')}</span>
              </div>
            ) : null}

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>#</th>
                    {preview.headers.map((h) => (
                      <th key={h} style={{ ...thStyle, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                    <th style={thStyle} />
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} style={{ background: r.__error ? 'var(--st-req-l)' : 'transparent' }}>
                      <td className="mono" style={{ padding: '7px 12px', color: 'var(--text-3)', borderBottom: '1px solid var(--line)' }}>{i + 1}</td>
                      {preview.headers.map((h) => (
                        <td
                          key={h}
                          style={{ padding: '7px 12px', borderBottom: '1px solid var(--line)', fontFamily: /Id|CSN|Code/.test(h) ? 'var(--mono)' : 'inherit' }}
                        >
                          {r[h] || <span style={{ color: 'var(--text-3)' }}>—</span>}
                        </td>
                      ))}
                      <td style={{ padding: '7px 12px', borderBottom: '1px solid var(--line)' }}>
                        {r.__error ? <span className="chip err">{ic.warn} {r.__error}</span> : <span className="chip ok">{ic.check} OK</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {outcome ? (
              <div
                style={{
                  padding: '10px 14px', borderTop: '1px solid var(--line)', fontSize: 12.5,
                  background: outcome.errors.length ? 'var(--st-req-l)' : 'var(--st-read-l)',
                  color: outcome.errors.length ? 'var(--st-req)' : 'var(--st-read)',
                }}
              >
                <div style={{ fontWeight: 700 }}>
                  {outcome.dryRun
                    ? (lang === 'es' ? 'Resultado de la comprobación' : 'Check result')
                    : (lang === 'es' ? 'Resultado' : 'Result')}
                </div>
                <div style={{ marginTop: 2 }}>
                  {outcome.created} {lang === 'es' ? 'fichas nuevas' : 'new records'} ·{' '}
                  {outcome.updated} {lang === 'es' ? 'con cambios' : 'changed'} ·{' '}
                  {outcome.unchanged} {lang === 'es' ? 'iguales que ahora' : 'already up to date'}
                </div>
                {outcome.errors.length ? (
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {outcome.errors.slice(0, 8).map((e) => (
                      <li key={`${e.row}-${e.id}`}>
                        {lang === 'es' ? 'Fila' : 'Row'} {e.row}
                        {e.id ? ` (${e.id})` : ''}: {e.message}
                      </li>
                    ))}
                    {outcome.errors.length > 8 ? (
                      <li>… {outcome.errors.length - 8} {lang === 'es' ? 'más' : 'more'}</li>
                    ) : null}
                  </ul>
                ) : null}
              </div>
            ) : null}

            <div style={{ padding: '10px 14px', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn" onClick={() => { setPreview(null); setFile(null); setOutcome(null); }}>
                {t('common.cancel')}
              </button>
              {IS_API ? (
                <>
                  <button className="btn" onClick={() => void enviar(true)} disabled={busy || !preview.valid}>
                    {ic.shield} {lang === 'es' ? 'Comprobar' : 'Check'} ({preview.valid})
                  </button>
                  <button
                    className="btn primary"
                    onClick={() => void enviar(false)}
                    disabled={busy || !comprobado}
                    data-tip={comprobado ? undefined : (lang === 'es' ? 'Comprueba primero.' : 'Run the check first.')}
                  >
                    {ic.check} {t('admin.apply')} ({preview.valid})
                  </button>
                </>
              ) : (
                <button className="btn primary" onClick={applyInMemory} disabled={!preview.valid}>
                  {ic.check} {t('admin.apply')} ({preview.valid})
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="card" style={{ padding: 28, textAlign: 'center', border: '1px dashed var(--line-2)', background: 'var(--surface-2)' }}>
            <div style={{ color: 'var(--text-3)', display: 'flex', justifyContent: 'center', marginBottom: 8 }}>{sized(ic.excel, 32)}</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
              {lang === 'es' ? 'Selecciona un archivo .xlsx o .csv para previsualizar el mapeo.' : 'Pick an .xlsx or .csv file to preview the mapping.'}
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

const thStyle: React.CSSProperties = {
  padding: '8px 12px', textAlign: 'left', fontSize: 10, color: 'var(--text-3)',
  textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.04em',
  background: 'var(--surface-2)', borderBottom: '1px solid var(--line)',
};
